import { useEffect, useRef, useState } from "react";
import { getSprintResumo, getSprintDevs, clearCache } from "../api/sprint.api";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend, Filler);

const REFRESH_SEC = 40;

export default function Burndown() {
  const [resumo,    setResumo]    = useState(null);
  const [devs,      setDevs]      = useState([]);
  const [countdown, setCountdown] = useState(REFRESH_SEC);
  const loadRef = useRef(null);

  async function load(isRefresh = false) {
    try {
      if (isRefresh) clearCache();
      const [r, d] = await Promise.all([getSprintResumo(), getSprintDevs()]);
      setResumo(r);
      setDevs(d.developers || []);
    } catch (err) {
      console.error(err);
    }
  }

  loadRef.current = load;

  function handleRefresh() {
    loadRef.current(true);
    setCountdown(REFRESH_SEC);
  }

  useEffect(() => {
    loadRef.current();
    setCountdown(REFRESH_SEC);
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { loadRef.current(true); return REFRESH_SEC; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  if (!resumo?.saida?.geral) return <BurndownSkeleton />;

  const { total, fechado: fechados, aberto: abertos } = resumo.saida.geral;
  const pct       = resumo.saida.geral.percentual ?? (total ? Math.round((fechados / total) * 100) : 0);
  const remaining = total - fechados;
  const heatColor = (p) => p >= 70 ? "#4ade80" : p >= 40 ? "#fbbf24" : "#f87171";

  /* ── Burndown estimado ── */
  /* distribui os fechamentos igualmente pelos 4 primeiros dias; último ponto = hoje */
  const step = fechados / 4;
  const realPoints = [
    total,
    Math.round(total - step),
    Math.round(total - step * 2),
    Math.round(total - step * 3),
    remaining,
  ];

  const burndownData = {
    labels: ["Dia 1", "Dia 2", "Dia 3", "Dia 4", "Hoje"],
    datasets: [
      {
        label: "Ideal",
        data: [total, Math.round(total * 0.75), Math.round(total * 0.5), Math.round(total * 0.25), 0],
        borderColor: "rgba(96,165,250,0.4)",
        borderDash: [6, 4],
        borderWidth: 2,
        pointRadius: 0,
        tension: 0,
        fill: false,
        datalabels: { display: false },
      },
      {
        label: "Real (estimado)",
        data: realPoints,
        borderColor: "#4ade80",
        borderWidth: 2.5,
        tension: 0.3,
        pointBackgroundColor: "#4ade80",
        pointBorderColor: "rgba(11,22,42,0.85)",
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        fill: "origin",
        backgroundColor: "rgba(74,222,128,0.07)",
        datalabels: {
          display: true,
          color: "#86efac",
          font: { size: 11, weight: "700", family: "Inter" },
          anchor: "top",
          align: "top",
          offset: 6,
          formatter: (v) => v,
        },
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 26, right: 8 } },
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        labels: {
          color: "#64748b",
          font: { size: 12, family: "Inter" },
          boxWidth: 14,
          padding: 16,
        },
      },
      tooltip: {
        backgroundColor: "rgba(11,22,42,0.95)",
        borderColor: "rgba(96,165,250,0.2)",
        borderWidth: 1,
        titleColor: "#94a3b8",
        bodyColor: "#e2e8f0",
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${ctx.raw} tickets restantes`,
        },
      },
      datalabels: { display: false },
    },
    scales: {
      x: {
        grid:  { color: "rgba(96,165,250,0.06)" },
        ticks: { color: "#94a3b8", font: { size: 11 } },
        border: { display: false },
      },
      y: {
        grid:  { color: "rgba(96,165,250,0.06)" },
        ticks: { color: "#64748b", font: { size: 11 } },
        border: { display: false },
        beginAtZero: true,
        suggestedMax: total + Math.ceil(total * 0.1),
      },
    },
  };

  /* ── Forecast ── */
  const velocity    = step; // tickets fechados por dia (média estimada)
  const daysToClose = velocity > 0 ? Math.ceil(remaining / velocity) : null;
  const forecastTxt =
    daysToClose === null ? "Sem dados" :
    daysToClose <= 0     ? "Concluído" :
    `${daysToClose} dia${daysToClose !== 1 ? "s" : ""}`;
  const forecastColor =
    daysToClose === null ? "#94a3b8" :
    daysToClose <= 0     ? "#4ade80" :
    daysToClose <= 3     ? "#fbbf24" : "#f87171";

  /* ── Devs com prog + emerg combinados, ordenados pelo mesmo critério da Sprint ── */
  const devsRanked = devs
    .map(d => {
      const prog = d.programado || { total: 0, aberto: 0, fechado: 0 };
      const emrg = d.emergencia || { total: 0, aberto: 0, fechado: 0 };
      const combined = {
        total:  prog.total  + emrg.total,
        fechado:prog.fechado + emrg.fechado,
        aberto: prog.aberto  + emrg.aberto,
      };
      const p     = combined.total ? Math.round((combined.fechado / combined.total) * 100) : 0;
      const score = combined.fechado * 20 + p * 0.5 - combined.aberto * 3;
      return { ...d, prog, emrg, combined, pct: p, score };
    })
    .sort((a, b) => b.score - a.score);

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div style={headerLeft}>
          <h1 style={h1}>Burndown</h1>
          <span style={badge}>Sprint Atual</span>
        </div>
        <div style={headerRight}>
          <StatChip label="Total"     value={total}    color="#60a5fa" />
          <StatChip label="Fechados"  value={fechados}  color="#4ade80" />
          <StatChip label="Abertos"   value={abertos}   color="#f87171" />
          <StatChip label="Progresso" value={`${pct}%`} color={heatColor(pct)} />
          <div style={dividerV} />
          <button
            onClick={handleRefresh}
            title="Atualizar agora"
            style={refreshBtn}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(96,165,250,0.18)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(96,165,250,0.08)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.2)"; }}
          >↻</button>
          <CountdownRing seconds={countdown} total={REFRESH_SEC} />
        </div>
      </header>

      {/* ── Grid ── */}
      <div style={grid}>

        {/* ── Gráfico ── */}
        <div style={glass}>
          <div style={chartHeader}>
            <SectionTitle>Tendência de Conclusão</SectionTitle>
            <span style={estimBadge}>Dados estimados</span>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <Line data={burndownData} options={chartOptions} plugins={[ChartDataLabels]} />
          </div>

          {/* ── Cards de forecast ── */}
          <div style={forecastRow}>
            <ForecastCard label="Fechados"   value={fechados}                                           sub={`de ${total} tickets`}    color="#4ade80" />
            <ForecastCard label="Restantes"  value={remaining}                                          sub={`${pct}% concluído`}      color={heatColor(pct)} />
            <ForecastCard label="Velocidade" value={velocity > 0 ? `${velocity.toFixed(1)}/dia` : "–"} sub="tickets por dia (média)"  color="#60a5fa" />
            <ForecastCard label="Previsão"   value={forecastTxt}                                        sub="para concluir restantes"  color={forecastColor} />
          </div>
        </div>

        {/* ── Desempenho por dev ── */}
        <div style={{ ...glass, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <SectionTitle>Desempenho por Desenvolvedor</SectionTitle>
          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
            {devsRanked.map((d, i) => {
              const c = heatColor(d.pct);
              const progPct = d.prog.total ? Math.round((d.prog.fechado / d.prog.total) * 100) : 0;
              const emrgPct = d.emrg.total ? Math.round((d.emrg.fechado / d.emrg.total) * 100) : 0;
              return (
                <div key={i} style={devCard}>
                  {/* Nome + % geral */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={devName}>{d.nome}</span>
                    <span style={{ color: c, fontWeight: 800, fontSize: 17 }}>{d.pct}%</span>
                  </div>

                  {/* Barra geral */}
                  <div style={barTrack}>
                    <div style={{ height: "100%", borderRadius: 4, width: `${d.pct}%`, background: `linear-gradient(90deg, ${c}99, ${c})`, boxShadow: `0 0 8px ${c}40`, transition: "width 0.7s ease" }} />
                  </div>

                  {/* Breakdown programados */}
                  {d.prog.total > 0 && (
                    <div style={subRow}>
                      <span style={{ ...subLabel, color: "#22d3ee" }}>● Programados</span>
                      <span style={subNum}>{d.prog.total}</span>
                      <span style={subUnit}>total</span>
                      <span style={{ ...subNum, color: "#4ade80" }}>{d.prog.fechado} ✓</span>
                      <span style={{ ...subNum, color: "#f87171" }}>{d.prog.aberto} ●</span>
                      <span style={{ ...subNum, color: "#22d3ee", marginLeft: "auto" }}>{progPct}%</span>
                    </div>
                  )}

                  {/* Breakdown emergências */}
                  {d.emrg.total > 0 && (
                    <div style={subRow}>
                      <span style={{ ...subLabel, color: "#a78bfa" }}>● Emergências</span>
                      <span style={subNum}>{d.emrg.total}</span>
                      <span style={subUnit}>total</span>
                      <span style={{ ...subNum, color: "#4ade80" }}>{d.emrg.fechado} ✓</span>
                      <span style={{ ...subNum, color: "#f87171" }}>{d.emrg.aberto} ●</span>
                      <span style={{ ...subNum, color: "#a78bfa", marginLeft: "auto" }}>{emrgPct}%</span>
                    </div>
                  )}

                  {/* Pills totais */}
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    <Pill color="#94a3b8" label={`${d.combined.total} total`} />
                    <Pill color="#4ade80" label={`${d.combined.fechado} fechados`} />
                    <Pill color="#f87171" label={`${d.combined.aberto} abertos`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────── */

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 14, flexShrink: 0 }}>
      {children}
    </div>
  );
}

function StatChip({ label, value, color }) {
  return (
    <div style={{ padding: "5px 14px", borderRadius: 10, background: `${color}12`, border: `1px solid ${color}30`, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <span style={{ color, fontWeight: 700, fontSize: 16 }}>{value}</span>
      <span style={{ color: "#64748b", fontSize: 10 }}>{label}</span>
    </div>
  );
}

function ForecastCard({ label, value, sub, color }) {
  return (
    <div style={{ flex: 1, background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 9, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</span>
      <span style={{ fontSize: 20, fontWeight: 800, color, letterSpacing: -0.5, lineHeight: 1.1 }}>{value}</span>
      <span style={{ fontSize: 9, color: "#475569" }}>{sub}</span>
    </div>
  );
}

function Pill({ color, label }) {
  return (
    <span style={{ display: "inline-flex", padding: "2px 8px", borderRadius: 10, background: `${color}12`, border: `1px solid ${color}25`, color, fontSize: 10, fontWeight: 600 }}>
      {label}
    </span>
  );
}

function CountdownRing({ seconds, total }) {
  const r      = 11;
  const circ   = 2 * Math.PI * r;
  const filled = circ * (seconds / total);
  const urgent = seconds <= 10;
  const color  = urgent ? "#f87171" : "#60a5fa";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <svg width={30} height={30} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
        <circle cx={15} cy={15} r={r} fill="none" stroke="rgba(96,165,250,0.1)" strokeWidth={2.5} />
        <circle cx={15} cy={15} r={r} fill="none"
          stroke={color} strokeWidth={2.5}
          strokeDasharray={circ}
          strokeDashoffset={circ - filled}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s linear, stroke 0.3s" }}
        />
      </svg>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
        <span style={{ color, fontSize: 14, fontWeight: 800, letterSpacing: -0.5 }}>
          {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
        </span>
        <span style={{ color: "#334155", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5 }}>
          próx. atualiz.
        </span>
      </div>
    </div>
  );
}

function BurndownSkeleton() {
  return (
    <div style={page}>
      <div style={{ ...skel, height: 36, width: 260, marginBottom: 18 }} />
      <div style={grid}>
        <div style={{ ...skel, flex: 1 }} />
        <div style={{ ...skel, flex: 1 }} />
      </div>
    </div>
  );
}

/* ── Styles ─────────────────────────────────────────── */

const page = { display: "flex", flexDirection: "column", gap: 14, height: "calc(100vh - 56px)", overflow: "hidden" };

const header     = { display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 };
const headerLeft = { display: "flex", alignItems: "center", gap: 12 };
const headerRight= { display: "flex", alignItems: "center", gap: 10 };

const h1 = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", letterSpacing: -0.5 };

const badge = { padding: "3px 12px", borderRadius: 20, background: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.25)", color: "#60a5fa", fontSize: 12, fontWeight: 700 };

const dividerV = { width: 1, height: 28, background: "rgba(255,255,255,0.08)", flexShrink: 0 };

const refreshBtn = {
  width: 34, height: 34, borderRadius: 8,
  border: "1px solid rgba(96,165,250,0.2)",
  background: "rgba(96,165,250,0.08)",
  color: "#60a5fa", fontSize: 18, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  transition: "background 0.15s, border-color 0.15s",
  flexShrink: 0, lineHeight: 1,
};

const glass = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14,
  padding: "18px 20px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const grid = { display: "grid", gridTemplateColumns: "7fr 4fr", gap: 14, flex: 1, overflow: "hidden" };

const chartHeader = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexShrink: 0 };

const estimBadge = {
  fontSize: 9, fontWeight: 700, color: "#475569",
  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 6, padding: "2px 8px", letterSpacing: 0.6, textTransform: "uppercase",
};

const forecastRow = { display: "flex", gap: 8, marginTop: 12, flexShrink: 0 };

const barTrack = { height: 7, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden", margin: "2px 0 4px" };

const devCard = { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(96,165,250,0.06)", borderRadius: 10, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 };

const devName = { fontSize: 13, fontWeight: 700, color: "#e2e8f0" };

const subRow   = { display: "flex", alignItems: "center", gap: 6, fontSize: 11 };
const subLabel = { fontWeight: 700, fontSize: 10, minWidth: 42 };
const subNum   = { fontWeight: 600, color: "#94a3b8" };
const subUnit  = { color: "#475569", fontSize: 9 };

const skel = { borderRadius: 14, background: "rgba(255,255,255,0.04)" };
